import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  ArrowLeft, Mail, Phone, Building2, Calendar as CalendarIcon, CreditCard, 
  GraduationCap, Target, FileText, Sparkles, MapPin, Heart, Download, Shield,
  TrendingUp, Award, CheckCircle2, Loader2, IndianRupee, Calculator, Flame, History
} from 'lucide-react';
import ProvenanceTab from '../components/ProvenanceTab';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EmployeeDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [employee, setEmployee] = useState(null);
  const [skills, setSkills] = useState(null);
  const [contributions, setContributions] = useState([]);
  const [profileData, setProfileData] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [perfSummary, setPerfSummary] = useState(null);
  const [latestSalary, setLatestSalary] = useState(null);
  const [taxData, setTaxData] = useState(null);
  const [taxLoading, setTaxLoading] = useState(false);
  const [wittiesAudit, setWittiesAudit] = useState(null);
  const [wittiesLoading, setWittiesLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hikeOpen, setHikeOpen] = useState(false);
  const [hikePct, setHikePct] = useState('');
  const [hikeEffective, setHikeEffective] = useState('');
  const [hikeMedical, setHikeMedical] = useState('');
  const [approving, setApproving] = useState(false);

  const fetchEmployeeData = useCallback(async () => {
    try {
      const [empRes, skillsRes, contribRes, profileRes, docsRes, perfRes, salRes] = await Promise.allSettled([
        axios.get(`${API_URL}/api/employees/${id}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/skills/${id}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/contributions?employee_id=${id}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/employees/${id}/profile`, { withCredentials: true }),
        axios.get(`${API_URL}/api/employees/${id}/documents`, { withCredentials: true }),
        axios.get(`${API_URL}/api/performance/summary/${id}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/salary-revisions/${id}`, { withCredentials: true }),
      ]);
      if (empRes.status === 'fulfilled') setEmployee(empRes.value.data);
      if (skillsRes.status === 'fulfilled') setSkills(skillsRes.value.data);
      if (contribRes.status === 'fulfilled') setContributions(contribRes.value.data);
      if (profileRes.status === 'fulfilled') setProfileData(profileRes.value.data?.profile || {});
      if (docsRes.status === 'fulfilled') setDocuments(docsRes.value.data || []);
      if (perfRes.status === 'fulfilled') setPerfSummary(perfRes.value.data);
      if (salRes.status === 'fulfilled') {
        const revs = salRes.value.data?.revisions || [];
        setLatestSalary(revs.length > 0 ? revs[0] : null);
      }
    } catch (error) {
      console.error('Fetch employee error:', error);
      toast.error('Failed to load employee details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchEmployeeData();
  }, [fetchEmployeeData]);

  const openApproveHike = () => {
    const pct = perfSummary?.suggested_hike_percentage || 0;
    setHikePct(String(pct));
    setHikeEffective(new Date().toISOString().slice(0, 10));
    setHikeMedical(latestSalary?.medical_insurance ? String(latestSalary.medical_insurance) : '0');
    setHikeOpen(true);
  };

  const computeNewCtc = () => {
    if (!latestSalary?.ctc || !hikePct) return 0;
    return Math.round(latestSalary.ctc * (1 + parseFloat(hikePct) / 100));
  };

  // Wisewit formula: Net = Gross - EmpPF - PT, where Gross = monthly_ctc - EmployerPF - Medical
  const computeNet = (ctc, medicalMonthly) => {
    if (!ctc) return 0;
    const monthly = ctc / 12;
    const basic = monthly * 0.40;
    const employerPF = basic * 0.12;
    const med = parseFloat(medicalMonthly || 0) || 0;
    const gross = monthly - employerPF - med;
    const employeePF = basic * 0.12;
    const pt = 200;
    return Math.round(gross - employeePF - pt);
  };
  const currentNet = latestSalary?.net_salary || computeNet(latestSalary?.ctc, latestSalary?.medical_insurance);
  const newNet = computeNet(computeNewCtc(), hikeMedical);
  const netDelta = newNet - currentNet;

  const fetchTaxComputation = async () => {
    if (taxData) return;
    setTaxLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/tax/computation/${id}`, { withCredentials: true });
      setTaxData(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load tax computation');
    } finally {
      setTaxLoading(false);
    }
  };

  const fetchWittiesAudit = async () => {
    if (wittiesAudit) return;
    setWittiesLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/witties/audit/${id}`, { withCredentials: true });
      setWittiesAudit(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load Witties audit');
    } finally {
      setWittiesLoading(false);
    }
  };

  const handleApproveHike = async () => {
    if (!latestSalary?.ctc) { toast.error('No existing salary structure to revise'); return; }
    if (!hikePct || parseFloat(hikePct) <= 0) { toast.error('Hike percentage must be greater than 0'); return; }
    setApproving(true);
    try {
      await axios.post(`${API_URL}/api/salary-structures/from-ctc`, {
        employee_id: id,
        ctc: computeNewCtc(),
        effective_from: hikeEffective,
        medical_insurance: parseFloat(hikeMedical || '0'),
      }, { withCredentials: true });
      toast.success(`Hike of ${hikePct}% approved — new CTC ₹${computeNewCtc().toLocaleString()}`);
      setHikeOpen(false);
      fetchEmployeeData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve hike');
    } finally {
      setApproving(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Employee not found</p>
        <Button variant="outline" onClick={() => navigate('/employees')} className="mt-4">
          Back to Employees
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="employee-detail-page">
      <Button variant="ghost" onClick={() => navigate('/employees')} className="gap-2">
        <ArrowLeft className="w-4 h-4" />
        Back to Employees
      </Button>

      {/* Profile Header */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row gap-6">
            <Avatar className="h-24 w-24">
              <AvatarImage src={employee.profile_picture} />
              <AvatarFallback className="bg-primary text-primary-foreground text-2xl">
                {getInitials(employee.name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div>
                  <h1 className="text-2xl font-bold">{employee.name}</h1>
                  <p className="text-lg text-muted-foreground">{employee.designation}</p>
                  <Badge variant="outline" className="mt-2">{employee.employee_id}</Badge>
                </div>
                <Badge className={employee.status === 'active' ? 'badge-success' : 'badge-danger'}>
                  {employee.status}
                </Badge>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="w-4 h-4 text-muted-foreground" />
                  <span>{employee.email}</span>
                </div>
                {employee.phone && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    <span>{employee.phone}</span>
                  </div>
                )}
                {employee.department && (
                  <div className="flex items-center gap-2 text-sm">
                    <Building2 className="w-4 h-4 text-muted-foreground" />
                    <span>{employee.department}</span>
                  </div>
                )}
                {employee.date_of_joining && (
                  <div className="flex items-center gap-2 text-sm">
                    <CalendarIcon className="w-4 h-4 text-muted-foreground" />
                    <span>Joined {new Date(employee.date_of_joining).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                  </div>
                )}
              </div>
              {employee.tech_stack?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {employee.tech_stack.map((tech, i) => (
                    <Badge key={i} variant="secondary" className="font-mono text-xs">{tech}</Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Performance Summary & Hike Approval */}
      {perfSummary && (perfSummary.latest_review || perfSummary.total_reviews > 0 || perfSummary.badge_count > 0) && (
        <Card className="border-emerald-200 dark:border-emerald-900/50 bg-gradient-to-br from-emerald-50/50 to-transparent dark:from-emerald-900/10" data-testid="perf-summary-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-emerald-100 dark:bg-emerald-900/30">
                  <TrendingUp className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Performance Snapshot</p>
                  <div className="flex items-center gap-2 mt-1">
                    {perfSummary.rating_level ? (
                      <Badge style={{ backgroundColor: perfSummary.rating_level.color + '20', color: perfSummary.rating_level.color, border: `1px solid ${perfSummary.rating_level.color}40` }} className="font-semibold">
                        {perfSummary.rating_level.label}
                      </Badge>
                    ) : <Badge variant="outline">No rating yet</Badge>}
                    {perfSummary.latest_review && (
                      <span className="text-sm text-muted-foreground">Score: <span className="font-bold text-foreground">{perfSummary.latest_review.overall_score}</span> ({perfSummary.latest_review.period})</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="text-center">
                  <p className="text-xs text-muted-foreground">Suggested Hike</p>
                  <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{perfSummary.suggested_hike_percentage || 0}%</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-muted-foreground flex items-center gap-1"><Award className="w-3 h-3" /> Badges</p>
                  <p className="text-2xl font-bold">{perfSummary.badge_count || 0}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-muted-foreground">Reviews</p>
                  <p className="text-2xl font-bold">{perfSummary.total_reviews || 0}</p>
                </div>
                {perfSummary.suggested_hike_percentage > 0 && latestSalary?.ctc && (
                  <Button onClick={openApproveHike} className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="approve-hike-btn">
                    <CheckCircle2 className="w-4 h-4" /> Approve Next Hike
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs defaultValue="profile" className="space-y-4" onValueChange={(v) => { if (v === 'tax') fetchTaxComputation(); if (v === 'witties') fetchWittiesAudit(); }}>
        <TabsList>
          <TabsTrigger value="profile" className="gap-2">
            <CreditCard className="w-4 h-4" />
            Profile Details
          </TabsTrigger>
          <TabsTrigger value="documents" className="gap-2">
            <FileText className="w-4 h-4" />
            Documents
          </TabsTrigger>
          <TabsTrigger value="skills" className="gap-2">
            <Sparkles className="w-4 h-4" />
            Skills
          </TabsTrigger>
          <TabsTrigger value="contributions" className="gap-2">
            <Target className="w-4 h-4" />
            Contributions
          </TabsTrigger>
          <TabsTrigger value="tax" className="gap-2" data-testid="tab-tax-computation">
            <Calculator className="w-4 h-4" />
            Tax Computation
          </TabsTrigger>
          <TabsTrigger value="witties" className="gap-2" data-testid="tab-witties-audit">
            <Flame className="w-4 h-4" />
            Witties Audit
          </TabsTrigger>
          <TabsTrigger value="provenance" className="gap-2" data-testid="tab-provenance">
            <History className="w-4 h-4" />
            Provenance
          </TabsTrigger>
        </TabsList>

        {/* Profile Details Tab (Admin view of employee's self-service profile) */}
        <TabsContent value="profile">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Contact */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2"><Phone className="w-4 h-4" /> Contact</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <InfoRow label="Phone" value={profileData?.phone || employee.phone} />
                <InfoRow label="Blood Group" value={profileData?.blood_group} />
              </CardContent>
            </Card>

            {/* Address */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2"><MapPin className="w-4 h-4" /> Address</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <InfoRow label="Address" value={profileData?.address} />
                <InfoRow label="City" value={profileData?.city} />
                <InfoRow label="State" value={profileData?.state} />
                <InfoRow label="Pincode" value={profileData?.pincode} />
              </CardContent>
            </Card>

            {/* Emergency Contact */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2"><Heart className="w-4 h-4" /> Emergency Contact</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <InfoRow label="Name" value={profileData?.emergency_contact_name} />
                <InfoRow label="Phone" value={profileData?.emergency_contact_phone} />
                <InfoRow label="Relation" value={profileData?.emergency_contact_relation} />
              </CardContent>
            </Card>

            {/* Bank & ID */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2"><Shield className="w-4 h-4" /> Bank & Compliance</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <InfoRow label="Bank Name" value={profileData?.bank_name} />
                <InfoRow label="Account No." value={profileData?.bank_account_number} sensitive />
                <InfoRow label="IFSC Code" value={profileData?.bank_ifsc} />
                <InfoRow label="PAN Number" value={profileData?.pan_number} />
                <InfoRow label="Aadhar Number" value={profileData?.aadhar_number} sensitive />
                <InfoRow label="UAN Number" value={profileData?.uan_number} />
                <InfoRow label="Passport Number" value={profileData?.passport_number} />
                <InfoRow label="Passport Issue Date" value={profileData?.passport_issue_date} />
                <InfoRow label="Passport Expiry Date" value={profileData?.passport_expiry_date} />
              </CardContent>
            </Card>
          </div>
          {!profileData || Object.keys(profileData).length === 0 ? (
            <p className="text-center text-muted-foreground mt-4 text-sm">Employee has not submitted profile details yet.</p>
          ) : null}
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><FileText className="w-4 h-4" /> Uploaded Documents</CardTitle>
              <CardDescription>Documents submitted by {employee.name} from their My Profile section</CardDescription>
            </CardHeader>
            <CardContent>
              {documents.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {documents.map((doc) => (
                    <div key={doc.id} className="flex items-center justify-between p-3 rounded-lg border bg-muted/20 hover:bg-muted/40 transition-colors" data-testid={`doc-${doc.id}`}>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">{doc.doc_label || doc.doc_type}</p>
                        <p className="text-xs text-muted-foreground truncate">{doc.original_filename}</p>
                        <p className="text-[10px] text-muted-foreground">{doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString('en-IN') : ''}</p>
                      </div>
                      <Button
                        variant="ghost" size="sm" className="gap-1 shrink-0 text-primary"
                        onClick={() => window.open(`${API_URL}/api/files/${doc.storage_path}`, '_blank')}
                        data-testid={`download-doc-${doc.id}`}
                      >
                        <Download className="w-3.5 h-3.5" /> View
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">No documents uploaded by this employee yet.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Skills Tab */}
        <TabsContent value="skills">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader><CardTitle className="text-lg">Tech Stack</CardTitle></CardHeader>
              <CardContent>
                {(skills?.tech_stack?.length > 0 || employee.tech_stack?.length > 0) ? (
                  <div className="flex flex-wrap gap-2">
                    {(skills?.tech_stack || employee.tech_stack || []).map((tech, i) => (
                      <Badge key={i} className="skill-badge">{tech}</Badge>
                    ))}
                  </div>
                ) : <p className="text-muted-foreground text-sm">No tech stack added</p>}
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-lg">AI/ML Models</CardTitle></CardHeader>
              <CardContent>
                {skills?.ai_models?.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {skills.ai_models.map((model, i) => <Badge key={i} className="badge-ai font-mono">{model}</Badge>)}
                  </div>
                ) : <p className="text-muted-foreground text-sm">No AI models added</p>}
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-lg">Frameworks</CardTitle></CardHeader>
              <CardContent>
                {skills?.frameworks?.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {skills.frameworks.map((fw, i) => <Badge key={i} variant="outline">{fw}</Badge>)}
                  </div>
                ) : <p className="text-muted-foreground text-sm">No frameworks added</p>}
              </CardContent>
            </Card>
            <Card className="md:col-span-2">
              <CardHeader><CardTitle className="text-lg flex items-center gap-2"><GraduationCap className="w-5 h-5" />Certifications</CardTitle></CardHeader>
              <CardContent>
                {skills?.certifications?.length > 0 ? (
                  <div className="space-y-2">
                    {skills.certifications.map((cert, i) => (
                      <div key={i} className="flex items-center gap-2 p-2 bg-muted rounded-md">
                        <div className="w-2 h-2 bg-primary rounded-full" /><span className="text-sm">{cert}</span>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-muted-foreground text-sm">No certifications added</p>}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Contributions Tab */}
        <TabsContent value="contributions">
          <Card>
            <CardHeader><CardTitle className="text-lg">Product Contributions</CardTitle></CardHeader>
            <CardContent>
              {contributions.length > 0 ? (
                <div className="space-y-4">
                  {contributions.map((contrib) => (
                    <div key={contrib.id} className="p-4 border border-border rounded-md">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h4 className="font-medium">{contrib.feature_name}</h4>
                          <p className="text-sm text-muted-foreground">{contrib.product_name}</p>
                          {contrib.description && <p className="text-sm mt-2">{contrib.description}</p>}
                        </div>
                        <Badge className={contrib.status === 'completed' ? 'badge-success' : contrib.status === 'in_progress' ? 'badge-warning' : 'badge-danger'}>
                          {contrib.status.replace('_', ' ')}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : <p className="text-muted-foreground text-sm">No contributions recorded</p>}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tax Computation Tab */}
        <TabsContent value="tax">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Calculator className="w-4 h-4" />
                Income Tax Projection — {taxData?.financial_year ? `FY ${taxData.financial_year}` : 'Current FY'}
              </CardTitle>
              <CardDescription>New Regime projection based on payslips paid so far + projected remaining months at current monthly gross.</CardDescription>
            </CardHeader>
            <CardContent>
              {taxLoading ? (
                <div className="flex items-center gap-2 text-muted-foreground text-sm py-8 justify-center">
                  <Loader2 className="w-4 h-4 animate-spin" /> Computing tax projection…
                </div>
              ) : !taxData ? (
                <p className="text-muted-foreground text-sm">Tax projection unavailable. Ensure a salary structure is set up.</p>
              ) : (
                <div className="space-y-5">
                  {/* Headline numbers */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="rounded-lg border bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800 p-4">
                      <p className="text-xs uppercase tracking-wide text-emerald-700 dark:text-emerald-300">Projected Annual Taxable Income</p>
                      <p className="text-2xl font-bold text-emerald-800 dark:text-emerald-200 mt-1" data-testid="taxable-income">₹{(taxData.taxable_income || 0).toLocaleString('en-IN')}</p>
                      <p className="text-xs text-emerald-700 dark:text-emerald-300 mt-1">After ₹{(taxData.standard_deduction || 0).toLocaleString('en-IN')} standard deduction</p>
                    </div>
                    <div className="rounded-lg border bg-rose-50 dark:bg-rose-950/30 border-rose-200 dark:border-rose-800 p-4">
                      <p className="text-xs uppercase tracking-wide text-rose-700 dark:text-rose-300">Annual Tax Liability</p>
                      <p className="text-2xl font-bold text-rose-800 dark:text-rose-200 mt-1" data-testid="annual-tax-liability">₹{(taxData.tax_liability || 0).toLocaleString('en-IN')}</p>
                      <p className="text-xs text-rose-700 dark:text-rose-300 mt-1">
                        {taxData.rebate_87a_applied
                          ? '87A rebate applied (taxable ≤ ₹7,00,000)'
                          : 'Includes 4% Health & Education Cess'}
                      </p>
                    </div>
                  </div>

                  {/* Breakdown */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="rounded-lg border p-4 space-y-2">
                      <p className="font-medium text-sm">Year-To-Date</p>
                      <div className="text-sm space-y-1.5">
                        <div className="flex justify-between"><span className="text-muted-foreground">Months paid</span><span className="font-medium">{taxData.ytd.months_paid}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">Gross earned</span><span className="font-medium">₹{(taxData.ytd.gross_earned || 0).toLocaleString('en-IN')}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">TDS deducted</span><span className="font-medium">₹{(taxData.ytd.tds_deducted || 0).toLocaleString('en-IN')}</span></div>
                      </div>
                    </div>
                    <div className="rounded-lg border p-4 space-y-2">
                      <p className="font-medium text-sm">Projection</p>
                      <div className="text-sm space-y-1.5">
                        <div className="flex justify-between"><span className="text-muted-foreground">Monthly gross</span><span className="font-medium">₹{(taxData.projection.monthly_full_pay || 0).toLocaleString('en-IN')}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">Months remaining</span><span className="font-medium">{taxData.projection.months_remaining}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">Projected remaining gross</span><span className="font-medium">₹{(taxData.projection.projected_remaining_gross || 0).toLocaleString('en-IN')}</span></div>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg border p-4 space-y-2">
                    <p className="font-medium text-sm">Annual Summary</p>
                    <div className="text-sm space-y-1.5">
                      <div className="flex justify-between"><span className="text-muted-foreground">Annual gross (YTD + projection)</span><span className="font-medium">₹{(taxData.annual_gross || 0).toLocaleString('en-IN')}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Less: Standard deduction</span><span className="font-medium">- ₹{(taxData.standard_deduction || 0).toLocaleString('en-IN')}</span></div>
                      <div className="flex justify-between border-t pt-1.5"><span className="font-medium">Taxable income</span><span className="font-semibold">₹{(taxData.taxable_income || 0).toLocaleString('en-IN')}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Total tax liability (incl. cess)</span><span className="font-medium">₹{(taxData.tax_liability || 0).toLocaleString('en-IN')}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">TDS remaining for FY</span><span className="font-medium">₹{(taxData.tds_remaining || 0).toLocaleString('en-IN')}</span></div>
                      <div className="flex justify-between border-t pt-1.5"><span className="font-medium">Suggested monthly TDS</span><span className="font-semibold text-amber-700 dark:text-amber-300" data-testid="suggested-monthly-tds">₹{(taxData.suggested_monthly_tds || 0).toLocaleString('en-IN')}</span></div>
                    </div>
                  </div>

                  <details className="rounded-lg border p-3 text-xs text-muted-foreground">
                    <summary className="cursor-pointer font-medium text-foreground">Notes & methodology</summary>
                    <ul className="list-disc pl-5 space-y-1 mt-2">
                      {(taxData.notes || []).map((n, i) => <li key={i}>{n}</li>)}
                    </ul>
                  </details>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Witties Audit Tab */}
        <TabsContent value="witties">
          <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-3">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <Flame className="w-4 h-4 text-amber-500" />
                  Witties Audit Log
                </CardTitle>
                <CardDescription>Every Witties event for this employee — earned, redeemed, corrections — newest first.</CardDescription>
              </div>
              {(user?.role === 'admin' || user?.role === 'hr') && wittiesAudit && (
                <Button size="sm" variant="outline" className="gap-1.5" data-testid="witties-csv-download"
                  onClick={() => {
                    window.open(`${API_URL}/api/witties/audit/${id}/csv`, '_blank');
                  }}>
                  <Download className="w-3.5 h-3.5" /> CSV
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {wittiesLoading ? (
                <div className="flex items-center gap-2 text-muted-foreground text-sm py-8 justify-center">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading audit log…
                </div>
              ) : !wittiesAudit ? (
                <p className="text-muted-foreground text-sm">No data.</p>
              ) : (
                <div className="space-y-5">
                  {/* Summary chips */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                    <div className="rounded-lg border bg-amber-50/50 dark:bg-amber-950/20 p-3">
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Total Earned</p>
                      <p className="text-lg font-semibold text-amber-700 dark:text-amber-300" data-testid="audit-total">{wittiesAudit.balance.total}</p>
                    </div>
                    <div className="rounded-lg border p-3">
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Available</p>
                      <p className="text-lg font-semibold">{wittiesAudit.balance.available}</p>
                    </div>
                    <div className="rounded-lg border p-3">
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Redeemed</p>
                      <p className="text-lg font-semibold">{wittiesAudit.balance.redeemed}</p>
                    </div>
                    <div className="rounded-lg border p-3">
                      <p className="text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-400">Wordle</p>
                      <p className="text-lg font-semibold text-emerald-700 dark:text-emerald-400">{wittiesAudit.summary_by_source.wordle}</p>
                    </div>
                    <div className="rounded-lg border p-3">
                      <p className="text-[10px] uppercase tracking-wider text-purple-700 dark:text-purple-400">Cortex</p>
                      <p className="text-lg font-semibold text-purple-700 dark:text-purple-400">{wittiesAudit.summary_by_source.cortex}</p>
                    </div>
                    <div className="rounded-lg border p-3">
                      <p className="text-[10px] uppercase tracking-wider text-blue-700 dark:text-blue-400">HR</p>
                      <p className="text-lg font-semibold text-blue-700 dark:text-blue-400">{wittiesAudit.summary_by_source.hr}</p>
                    </div>
                  </div>

                  {/* Event list */}
                  {wittiesAudit.events.length === 0 ? (
                    <p className="text-center text-sm text-muted-foreground py-8">No Witties events recorded yet.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="data-table w-full">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Source</th>
                            <th>Reason</th>
                            <th className="text-right">Witties</th>
                          </tr>
                        </thead>
                        <tbody>
                          {wittiesAudit.events.map((ev) => {
                            const meta = {
                              wordle: { label: 'Wordle', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300' },
                              cortex: { label: 'Cortex', cls: 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950/30 dark:text-purple-300' },
                              hr: { label: 'HR', cls: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/30 dark:text-blue-300' },
                              redemption: { label: 'Redeemed', cls: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300' },
                              correction: { label: 'Correction', cls: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/30 dark:text-rose-300' },
                              other: { label: 'Other', cls: 'bg-zinc-100 text-zinc-700 border-zinc-200 dark:bg-zinc-900/50 dark:text-zinc-300' },
                            }[ev.category];
                            return (
                              <tr key={ev.id} data-testid={`audit-event-${ev.id}`}>
                                <td className="text-xs whitespace-nowrap">
                                  {new Date(ev.created_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                </td>
                                <td>
                                  <span className={`inline-flex text-[10px] font-medium px-2 py-0.5 rounded border ${meta.cls}`}>{meta.label}</span>
                                </td>
                                <td className="text-sm">{ev.reason || '—'}</td>
                                <td className={`text-right font-semibold ${ev.amount >= 0 ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-700 dark:text-rose-300'}`}>
                                  {ev.amount >= 0 ? '+' : ''}{ev.amount}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        {/* Provenance Tab */}
        <TabsContent value="provenance">
          <ProvenanceTab employeeId={id} canEdit={user?.role === 'admin' || user?.role === 'hr'} />
        </TabsContent>
      </Tabs>

      {/* Approve Next Hike Dialog */}
      <Dialog open={hikeOpen} onOpenChange={setHikeOpen}>
        <DialogContent className="sm:max-w-md" data-testid="approve-hike-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><IndianRupee className="w-5 h-5 text-emerald-600" /> Approve Next Hike</DialogTitle>
            <DialogDescription>Creates a new salary structure (effective from selected date) using the Wisewit CTC formula.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {latestSalary?.ctc ? (
              <div className="rounded-lg border bg-muted/40 p-3 text-sm space-y-1">
                <div className="flex justify-between"><span className="text-muted-foreground">Current CTC</span><span className="font-mono font-semibold">₹{latestSalary.ctc.toLocaleString()}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Hike</span><span className="font-mono font-semibold text-emerald-600">+{hikePct || 0}%</span></div>
                <div className="flex justify-between border-t pt-1 mt-1"><span className="text-muted-foreground">New CTC</span><span className="font-mono font-bold text-emerald-700 dark:text-emerald-400">₹{computeNewCtc().toLocaleString()}</span></div>
              </div>
            ) : (
              <p className="text-sm text-amber-600 dark:text-amber-400">No existing salary structure. Please create one first via Payroll.</p>
            )}
            {/* Hike Impact Preview — Net Take Home */}
            {latestSalary?.ctc && hikePct && parseFloat(hikePct) > 0 && (
              <div className="rounded-lg border border-emerald-200 dark:border-emerald-900/50 bg-emerald-50/40 dark:bg-emerald-900/10 p-3 text-sm space-y-1" data-testid="hike-impact-preview">
                <p className="text-xs uppercase tracking-wider font-semibold text-emerald-700 dark:text-emerald-400 mb-1">Hike Impact — Monthly Net Take-Home</p>
                <div className="flex justify-between"><span className="text-muted-foreground">Current Net</span><span className="font-mono">₹{currentNet.toLocaleString()}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">New Net</span><span className="font-mono font-bold text-emerald-700 dark:text-emerald-400">₹{newNet.toLocaleString()}</span></div>
                <div className="flex justify-between border-t pt-1 mt-1"><span className="text-muted-foreground">Monthly Increase</span><span className="font-mono font-bold text-emerald-600">+₹{netDelta.toLocaleString()}/mo</span></div>
              </div>
            )}
            <div className="space-y-2">
              <Label>Hike Percentage (%)</Label>
              <Input type="number" min="0" max="100" step="0.5" value={hikePct} onChange={e => setHikePct(e.target.value)} data-testid="hike-pct-input" />
              {perfSummary?.suggested_hike_percentage > 0 && (
                <p className="text-xs text-muted-foreground">Suggested by Performance: {perfSummary.suggested_hike_percentage}% ({perfSummary.rating_level?.label})</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Effective From</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="hike-effective-date">
                      {hikeEffective ? format(new Date(hikeEffective), 'dd MMM yyyy') : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={hikeEffective ? new Date(hikeEffective) : undefined} onSelect={(d) => d && setHikeEffective(format(d, 'yyyy-MM-dd'))} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>Medical Insurance (₹/mo)</Label>
                <Input type="number" value={hikeMedical} onChange={e => setHikeMedical(e.target.value)} placeholder="0" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHikeOpen(false)}>Cancel</Button>
            <Button onClick={handleApproveHike} disabled={approving || !latestSalary?.ctc} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2" data-testid="confirm-hike-btn">
              {approving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Approve & Create Revision
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const InfoRow = ({ label, value, sensitive }) => (
  <div className="flex items-center justify-between">
    <span className="text-muted-foreground">{label}</span>
    <span className={`font-medium ${sensitive ? 'font-mono' : ''}`}>
      {value || <span className="text-muted-foreground/50 italic">Not provided</span>}
    </span>
  </div>
);

export default EmployeeDetailPage;
